import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { DataAccessService } from 'src/app/shared/services/data-access.service';
import { catchError, map, mergeMap, switchMap, withLatestFrom, concatMap, take, tap } from 'rxjs/operators';
import { of, from, firstValueFrom, forkJoin } from 'rxjs';
import { Store } from '@ngrx/store';
import * as ResponseActions from '../Actions/responses.actions';
import * as ResponseSelectors from '../Selectors/responses.selectors';

@Injectable()
export class ResponseEffects {

          // Load Responses Effect
          loadResponses$ = createEffect(() =>
            this.actions$.pipe(
              ofType(ResponseActions.loadResponses),
              switchMap(({ botId, collection }) => {
                return this.dataAccessService.getResponseActionsListCondtion(collection, botId, true, false, false, false, false).pipe(
                  map(responses => {
                    return ResponseActions.loadResponsesSuccess({ responses, botId });
                  }),
                  catchError(error => {
                    return of(ResponseActions.loadResponsesFailure({ error: error.message }));
                  })
                );
              })
            )
          );

          // Load Flow Data Effect
          loadFlowData$ = createEffect(() =>
            this.actions$.pipe(
              ofType(ResponseActions.loadFlowData),
              mergeMap(({ botId, responseId, flowIds }) => {
                const flowObservables = flowIds.map(flowId => {
                  return this.dataAccessService.getFlowById(botId, flowId).pipe(
                    map(flow => flow),
                    catchError(error => {
                      return of(null);
                    })
                  );
                });
                
                return forkJoin(flowObservables).pipe(
                    map(flows => {
                        const validFlows = flows.filter(flow => flow !== null);
                        const linkedFlows = validFlows.map(flow => ({
                            flowId: flow.id,
                            name: flow.name,
                            flowData: flow
                        }));
                        return ResponseActions.loadFlowDataSuccess({ responseId, linkedFlows });
                    }),
                    catchError(error => {
                        return of(ResponseActions.loadFlowDataFailure({ responseId, error: error.message }));
                    })
                );
      })
    )
  );

          // Auto-load Flow Data for Responses with flowIds
          autoLoadFlowData$ = createEffect(() =>
            this.actions$.pipe(
              ofType(ResponseActions.loadResponsesSuccess),
              mergeMap(({ responses, botId }) => {
                const responsesWithFlows = responses.filter(response => response.flowIds && response.flowIds.length > 0);
                
                const flowDataActions = responsesWithFlows.map(response => {
                  return ResponseActions.loadFlowData({
                    botId: botId,
                    responseId: response.id,
                    flowIds: response.flowIds
                  });
                });
                
                return from(flowDataActions);
              })
            )
          );

  // Filter Responses Effect
  filterResponses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResponseActions.filterResponses),
      withLatestFrom(this.store.select(ResponseSelectors.selectAllResponses)),
      map(([{ query }, responses]) => {
        const filtered = responses.filter(response => 
          response.name?.toLowerCase().includes(query.toLowerCase())
        );
        // Get botId from the first response (assuming all responses have the same botId)
        const botId = responses.length > 0 ? responses[0].botId || '' : '';
        return ResponseActions.loadResponsesSuccess({ responses: filtered, botId });
      })
    )
  );

  // Add Response Effect
  addResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResponseActions.addResponse),
      switchMap(({ response }) =>
        // Here you would typically make an API call to add the response
        // For now, we'll just simulate success
        of(ResponseActions.addResponseSuccess({ response })).pipe(
          catchError(error => of(ResponseActions.addResponseFailure({ error: error.message })))
        )
      )
    )
  );

  // Update Response Effect
  updateResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResponseActions.updateResponse),
      switchMap(({ responseId, updates }) =>
        // Here you would typically make an API call to update the response
        // For now, we'll just simulate success
        of(ResponseActions.updateResponseSuccess({ response: { id: responseId, ...updates } })).pipe(
          catchError(error => of(ResponseActions.updateResponseFailure({ error: error.message })))
        )
      )
    )
  );

  // Delete Response Effect
  deleteResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResponseActions.deleteResponse),
      switchMap(({ responseId }) =>
        // Here you would typically make an API call to delete the response
        // For now, we'll just simulate success
        of(ResponseActions.deleteResponseSuccess({ responseId })).pipe(
          catchError(error => of(ResponseActions.deleteResponseFailure({ error: error.message })))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private dataAccessService: DataAccessService,
    private store: Store
  ) {}
}
